/*
 * Where the boot meets the modules, and the only place it does (modules.md §8). `main.tsx` knows a
 * list and these two calls; what a module does with them is written in the module's own `bind.ts`.
 *
 *   · `installProvisions` puts what the modules offer core's own surfaces into the holders those
 *     surfaces read — who is online, the per-conversation notification rows. It runs before the first render on every page, a full page included,
 *     because a face drawn on the first paint asks for its dot from that paint on and the holder is
 *     never swapped afterwards. It installs every offer of the build, whether or not the instance
 *     runs that module: it runs before the bootstrap has said which do, and a surface of a module
 *     that is not running is one the reader never reaches.
 *   · `bindModules` hands every module the client, the outbox and the socket's request, in
 *     modules.ts order, once the outbox exists and before anything renders — a surface that came up
 *     before its module was bound would keep the unbound calls, because binding is not a state change
 *     React re-renders on. A full page skips it: it writes nothing, sends nothing and has no composer.
 */

import {
	clearConversationNotifications,
	registerConversationNotifications,
} from "../notify/overrides.ts"
import { setPresenceSource } from "../people/presence.ts"
import type { ModuleBindings, ModuleRegistry } from "./registry.ts"

/** Installs every module's offer to core's surfaces, and takes away what no module offers. */
export function installProvisions(registry: ModuleRegistry): void {
	setPresenceSource(registry.provided("presence") ?? null)
	const notifications = registry.provided("conversationNotifications")
	if (notifications === undefined) clearConversationNotifications()
	else registerConversationNotifications(notifications)
}

/**
 * Binds every module to the running client, and answers the unbinding of all of them, in the
 * reverse order. The boot keeps the bindings for the page's life — signing out reloads the page —
 * so only a test calls the answer.
 */
export function bindModules(registry: ModuleRegistry, b: ModuleBindings): () => void {
	const unbinds = registry.all().flatMap((mod) => (mod.bind === undefined ? [] : [mod.bind(b)]))
	return () => {
		for (const unbind of unbinds.reverse()) unbind()
	}
}
